import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

let listRequestId = 0;

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch, rootGetters }) => {
    listRequestId += 1;
    const requestId = listRequestId;
    const accountId = ConversationApi.accountIdFromRoute;
    const userId = rootGetters?.getCurrentUserID;
    const isCurrent = () =>
      requestId === listRequestId &&
      accountId === ConversationApi.accountIdFromRoute &&
      userId === rootGetters?.getCurrentUserID;
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get(params);
      if (!isCurrent()) return;
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      if (isCurrent()) commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

  fetchFilteredConversations: async (
    { commit, dispatch, rootGetters },
    params
  ) => {
    listRequestId += 1;
    const requestId = listRequestId;
    const accountId = ConversationApi.accountIdFromRoute;
    const userId = rootGetters?.getCurrentUserID;
    const isCurrent = () =>
      requestId === listRequestId &&
      accountId === ConversationApi.accountIdFromRoute &&
      userId === rootGetters?.getCurrentUserID;
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      if (!isCurrent()) return null;
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
      return data;
    } catch (error) {
      if (isCurrent()) commit(types.CLEAR_LIST_LOADING_STATUS);
      return null;
    }
  },

  refreshContactConversations: async (
    { commit, state },
    { contactId, params, queryData }
  ) => {
    const contactConversations = state.allConversations.filter(
      conversation => conversation.meta?.sender?.id === contactId
    );
    // Recheck access without clearing the open chat, its messages or pagination.
    await Promise.all(
      contactConversations.map(async conversation => {
        try {
          const { data } = await ConversationApi.show(conversation.id);
          commit(types.SET_ALL_CONVERSATION, [data]);
        } catch (error) {
          if ([401, 403, 404].includes(error.response?.status)) {
            commit(types.DELETE_CONVERSATION, conversation.id);
          }
        }
      })
    );

    // Include newly accessible conversations using the current list's filters.
    try {
      const response = queryData
        ? await ConversationApi.filter({ queryData, page: 1 })
        : await ConversationApi.get(params);
      const { payload } = queryData ? response.data : response.data.data;
      commit(types.SET_ALL_CONVERSATION, payload);
    } catch {
      // Keep the current list when the background refresh cannot be completed.
    }
  },

  emptyAllConversations({ commit }, options = {}) {
    listRequestId += 1;
    commit(types.EMPTY_ALL_CONVERSATION, options);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    let lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const recoveredMessages = [];
    let metadata;
    let hasMore = true;
    while (hasMore) {
      // Each page needs the cursor returned by the previous request.
      // eslint-disable-next-line no-await-in-loop
      const response = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      const {
        data: { meta, payload },
      } = response;
      metadata = meta;
      recoveredMessages.push(...payload);
      hasMore = payload.length === 100;
      lastMessageId = payload.at(-1)?.id;
    }
    // Re-read the live state: HTTP list sync and WebSocket messages can arrive
    // while pages are loading. Failed requests propagate to ReconnectService.
    const currentChat = state.allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!currentChat) return;
    const existingIds = new Set(
      currentChat.messages.map(message => message.id)
    );
    const messages = [
      ...currentChat.messages,
      ...recoveredMessages.filter(message => !existingIds.has(message.id)),
    ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
      id: conversationId,
      data: metadata,
    });
    commit(types.SET_MISSING_MESSAGES, { id: conversationId, data: messages });
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: null,
    });
    dispatch('markMessagesRead', { id: conversationId }, { root: true });
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
    if (data.dataFetched === undefined) {
      try {
        await dispatch('fetchPreviousMessages', {
          after,
          before: data.messages[0].id,
          conversationId: data.id,
        });
        commit(types.SET_CHAT_DATA_FETCHED, data.id);
      } catch (error) {
        // Ignore error
      }
    }
  },

  assignAgent: async (
    { dispatch, state, commit },
    { conversationId, agentId, assigneeType = null }
  ) => {
    try {
      await dispatch('setConversationLastMessageId', { conversationId });
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
        assigneeType,
      });
      dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: response.data,
      });

      const conversation = state.allConversations.find(
        item => item.id === conversationId
      );
      const contactId = conversation?.meta?.sender?.id;
      if (contactId) {
        commit(
          'contacts/SET_CONTACT_ITEM',
          {
            id: contactId,
            assignee: response.data || null,
            assignee_type: agentId ? assigneeType || 'User' : null,
          },
          { root: true }
        );
      }
      await dispatch('syncActiveConversationMessages', { conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, { conversationId, assignee }) {
    commit(types.ASSIGN_AGENT, { conversationId, assignee });
  },

  assignTeam: async (
    { dispatch, state, commit },
    { conversationId, teamId }
  ) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });

      const conversation = state.allConversations.find(
        item => item.id === conversationId
      );
      const contactId = conversation?.meta?.sender?.id;
      if (contactId) {
        commit(
          'contacts/SET_CONTACT_ITEM',
          {
            id: contactId,
            assignee: response.data || null,
            assignee_type: teamId ? 'Team' : null,
          },
          { root: true }
        );
      }
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    {
      conversationId,
      status,
      snoozedUntil = null,
      customAttributes = null,
      closingNote = null,
    }
  ) => {
    try {
      // Update custom attributes first if provided
      if (customAttributes) {
        await ConversationApi.updateCustomAttributes({
          conversationId,
          customAttributes,
        });
        commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
          conversationId,
          customAttributes,
        });
      }

      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
        closingNote,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: response.data.status ?? MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: response.data.status ?? MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  async addMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    if (message.content_type === CONTENT_TYPES.VOICE_CALL) {
      const { handleVoiceCallCreated } = await import('dashboard/helper/voice');
      handleVoiceCallCreated(message, rootGetters?.getCurrentUserID);
    }
  },

  async updateMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.content_type === CONTENT_TYPES.VOICE_CALL) {
      const { handleVoiceCallUpdated } = await import('dashboard/helper/voice');
      handleVoiceCallUpdated(commit, message, rootGetters?.getCurrentUserID);
    }
  },

  reactToMessage: async ({ commit }, { conversationId, messageId, emoji }) => {
    const { data } = await MessageApi.react(conversationId, messageId, emoji);
    commit(types.UPDATE_MESSAGE_REACTIONS, data);
  },

  editMessageContent: async (
    { commit },
    { conversationId, messageId, content }
  ) => {
    const { data } = await MessageApi.edit(conversationId, messageId, content);
    commit(types.ADD_MESSAGE, data);
  },

  deleteMessage: async function deleteMessage(
    { commit },
    { conversationId, messageId, scope }
  ) {
    try {
      const { data } = scope
        ? await MessageApi.delete(conversationId, messageId, scope)
        : await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw error instanceof Error
        ? error
        : Object.assign(new Error(error.message), error);
    }
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnParticipatingView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch }, conversation) {
    const {
      meta: { sender },
    } = conversation;

    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  syncContactLabels({ commit }, { contactId, labels }) {
    commit(types.SYNC_CONTACT_LABELS_TO_CONVERSATIONS, {
      contactId,
      labels,
    });
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    const { conversationId, ...sender } = data;
    if (sender.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, sender);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, { conversationId, ...sender });
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    await ConversationApi.sendEmailTranscript({ conversationId, email });
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes: custom_attributes,
      });
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
